import { Controller } from "stimulus"
import { showLoader, hideLoader } from "../../utils/loader"
import * as Choices from 'choices.js/public/assets/scripts/choices'

export default class extends Controller {
  static targets = ["modal", "form", "organizationSelect", "groupsContainer", "groupSearch", "preserveGroupsCheckbox", "submitBtn", "warningContainer", "warningText"]

  connect() {
    this.initializeChoices()
    this.organizationsData = []
    this.organizationsLoaded = false

    if (this.hasModalTarget) {
      this.modalTarget.addEventListener('hidden.bs.modal', this.handleModalClose.bind(this))
    }
  }

  disconnect() {
    if (this.organizationChoices) {
      this.organizationChoices.destroy()
    }

    if (this.hasModalTarget) {
      this.modalTarget.removeEventListener('hidden.bs.modal', this.handleModalClose.bind(this))
    }
  }

  handleModalClose() {
    if (this.organizationChoices) {
      this.organizationChoices.removeActiveItems()
    }

    this.groupsContainerTarget.innerHTML = '<div class="text-muted text-center py-3">Select an organization to load groups</div>'

    if (this.hasGroupSearchTarget) {
      this.groupSearchTarget.style.display = 'none'
      this.groupSearchTarget.value = ''
    }

    if (this.hasPreserveGroupsCheckboxTarget) {
      this.preserveGroupsCheckboxTarget.checked = false
    }

    this.invoiceTypeWarning = null
    this.hideWarning()
  }

  initializeChoices() {
    // Initialize Choices.js for searchable organization select
    this.organizationChoices = new Choices(this.organizationSelectTarget, {
      searchEnabled: true,
      searchPlaceholderValue: 'Search organizations...',
      itemSelectText: '',
      shouldSort: false,
      placeholder: true,
      placeholderValue: 'Select organization...'
    })

    this.organizationSelectTarget.addEventListener('showDropdown', () => {
      if (!this.organizationsLoaded) {
        this.loadOrganizations()
      }
    })

    const choicesContainer = this.organizationSelectTarget.closest('.choices')
    if (choicesContainer) {
      choicesContainer.addEventListener('click', () => {
        if (!this.organizationsLoaded) {
          this.loadOrganizations()
        }
      }, { once: true })

      const searchInput = choicesContainer.querySelector('.choices__input--cloned')
      if (searchInput) {
        searchInput.addEventListener('focus', () => {
          if (!this.organizationsLoaded) {
            this.loadOrganizations()
          }
        }, { once: true })

        searchInput.addEventListener('input', (e) => {
          this.handleSearchInput(e.target.value)
        })
      }
    }
  }

  handleSearchInput(searchTerm) {
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout)
    }

    this.searchTimeout = setTimeout(() => {
      this.searchOrganizations(searchTerm)
    }, 300)
  }

  async searchOrganizations(searchTerm) {
    if (this.organizationsLoading) {
      return
    }

    this.organizationsLoading = true

    try {
      const url = new URL('/admin/organizations/autocomplete.json', window.location.origin)
      if (searchTerm && searchTerm.trim()) {
        url.searchParams.append('q', searchTerm.trim())
      }

      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) {
        throw new Error('Failed to load organizations')
      }

      const organizations = await response.json()
      this.populateOrganizations(organizations, false)
    } catch (error) {
      console.error('Error loading organizations:', error)
    } finally {
      this.organizationsLoading = false
    }
  }

  async loadOrganizations(searchTerm = '') {
    if (this.organizationsLoading) {
      return
    }

    this.organizationsLoading = true

    try {
      const url = new URL('/admin/organizations/autocomplete.json', window.location.origin)
      if (searchTerm) {
        url.searchParams.append('q', searchTerm)
      }

      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!response.ok) {
        throw new Error('Failed to load organizations')
      }

      const organizations = await response.json()
      this.populateOrganizations(organizations, true)
      this.organizationsLoaded = true
    } catch (error) {
      console.error('Error loading organizations:', error)
      alert('Failed to load organizations. Please refresh the page.')
    } finally {
      this.organizationsLoading = false
    }
  }

  populateOrganizations(organizations, updateCache = true) {
    if (updateCache) {
      this.organizationsData = organizations
    }

    this.organizationChoices.clearStore()

    // Add organizations to Choices.js
    const choices = organizations.map(org => ({
      value: org.id.toString(),
      label: org.name,
      selected: false
    }))

    this.organizationChoices.setChoices(choices, 'value', 'label', true)
  }

  async onOrganizationChange(event) {
    const organizationId = event.target.value

    if (!organizationId) {
      this.groupsContainerTarget.innerHTML = '<div class="text-muted text-center py-3">Select an organization to load groups</div>'
      this.invoiceTypeWarning = null
      this.hideWarning()
      return
    }

    try {
      showLoader()

      // Load groups
      const groupsResponse = await fetch(`/admin/organizations/${organizationId}/groups?format=json`, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      })

      if (!groupsResponse.ok) {
        throw new Error('Failed to load groups')
      }

      const groupsData = await groupsResponse.json()
      this.populateGroups(groupsData.groups || groupsData)

      // Check invoice types
      const targetOrg = this.organizationsData.find(org => org.id.toString() === organizationId)
      if (targetOrg?.invoice_type) {
        const userIds = this.getSelectedUserIds('table_selection_user_ids')

        if (userIds.length > 0) {
          const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
          const checkResponse = await fetch('/admin/users_bulk/check_invoice_types', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-CSRF-Token': csrfToken,
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: JSON.stringify({
              user_ids: userIds,
              target_invoice_type: targetOrg.invoice_type
            })
          })

          if (checkResponse.ok) {
            const data = await checkResponse.json()
            this.invoiceTypeWarning = data.warning

            if (data.warning) {
              this.showWarning(data.warning)
            } else {
              this.hideWarning()
            }
          }
        } else {
          this.hideWarning()
        }
      } else {
        this.hideWarning()
      }
    } catch (error) {
      console.error('Error loading groups:', error)
      this.groupsContainerTarget.innerHTML = '<div class="text-danger text-center py-3">Failed to load groups</div>'
    } finally {
      hideLoader()
    }
  }

  populateGroups(groups) {
    const container = this.groupsContainerTarget
    container.innerHTML = ''

    if (!groups || groups.length === 0) {
      container.innerHTML = '<div class="text-muted text-center py-3">No groups available for this organization</div>'
      this.groupSearchTarget.style.display = 'none'
      return
    }

    // Show search box when there are groups
    this.groupSearchTarget.style.display = 'block'
    this.groupSearchTarget.value = '' // Clear search

    // Store groups for filtering
    this.allGroups = groups

    // Add "Select All" checkbox
    const selectAllDiv = document.createElement('div')
    selectAllDiv.className = 'form-check mb-2 pb-2'
    selectAllDiv.style.borderBottom = '1px solid #dee2e6'
    selectAllDiv.innerHTML = `
      <input class="form-check-input" type="checkbox" id="selectAllGroups" data-action="change->users--bulk-move#toggleSelectAll">
      <label class="form-check-label" for="selectAllGroups">
        Select All Groups
      </label>
    `
    container.appendChild(selectAllDiv)

    // Add individual group checkboxes
    groups.forEach(group => {
      const checkboxDiv = document.createElement('div')
      checkboxDiv.className = 'form-check mb-2 group-item'
      checkboxDiv.dataset.groupName = group.name.toLowerCase()
      checkboxDiv.innerHTML = `
        <input class="form-check-input group-checkbox" type="checkbox" name="group_ids[]" value="${group.id}" id="group_${group.id}" data-action="change->users--bulk-move#updateSelectAll">
        <label class="form-check-label" for="group_${group.id}">
          ${this.escapeHtml(group.name)}
        </label>
      `
      container.appendChild(checkboxDiv)
    })
  }

  filterGroups(event) {
    const searchTerm = event.target.value.toLowerCase()
    const groupItems = this.groupsContainerTarget.querySelectorAll('.group-item')

    let visibleCount = 0
    groupItems.forEach(item => {
      const groupName = item.dataset.groupName
      if (groupName.includes(searchTerm)) {
        item.style.display = 'block'
        visibleCount++
      } else {
        item.style.display = 'none'
      }
    })

    // Update select all checkbox to only affect visible items
    this.updateSelectAll()
  }

  toggleSelectAll(event) {
    const isChecked = event.target.checked
    // Only toggle visible checkboxes
    const visibleItems = this.groupsContainerTarget.querySelectorAll('.group-item:not([style*="display: none"])')
    visibleItems.forEach(item => {
      const checkbox = item.querySelector('.group-checkbox')
      if (checkbox) {
        checkbox.checked = isChecked
      }
    })
  }

  updateSelectAll() {
    const selectAllCheckbox = this.groupsContainerTarget.querySelector('#selectAllGroups')
    if (!selectAllCheckbox) return

    // Only consider visible checkboxes
    const visibleItems = this.groupsContainerTarget.querySelectorAll('.group-item:not([style*="display: none"])')
    const visibleCheckboxes = Array.from(visibleItems).map(item => item.querySelector('.group-checkbox')).filter(cb => cb)

    if (visibleCheckboxes.length === 0) {
      selectAllCheckbox.checked = false
      selectAllCheckbox.indeterminate = false
      return
    }

    const allChecked = visibleCheckboxes.every(cb => cb.checked)
    const someChecked = visibleCheckboxes.some(cb => cb.checked)

    selectAllCheckbox.checked = allChecked
    selectAllCheckbox.indeterminate = someChecked && !allChecked
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  async submit(event) {
    event.preventDefault()

    const organizationId = this.organizationSelectTarget.value

    if (!organizationId) {
      alert('Please select an organization')
      return
    }

    const selectedGroupIds = Array.from(this.groupsContainerTarget.querySelectorAll('.group-checkbox:checked')).map(cb => cb.value)
    const preserveGroups = this.preserveGroupsCheckboxTarget.checked

    // Get selected user IDs from session storage
    const storageKey = 'table_selection_user_ids'
    const userIds = this.getSelectedUserIds(storageKey)

    if (userIds.length === 0) {
      alert('No users selected')
      return
    }

    // Build confirmation message
    const confirmMessage = `Move ${userIds.length} user(s) to the selected organization${selectedGroupIds.length > 0 ? ' and groups' : ''}?`

    if (!confirm(confirmMessage)) {
      return
    }

    showLoader()

    const formData = new FormData()
    formData.append('organization_id', organizationId)
    formData.append('preserve_groups', preserveGroups)

    selectedGroupIds.forEach(groupId => {
      formData.append('group_ids[]', groupId)
    })

    userIds.forEach(userId => {
      formData.append('user_ids[]', userId)
    })

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const headers = csrfToken ? { "X-CSRF-Token": csrfToken } : {}

    try {
      const response = await fetch(this.formTarget.action, {
        method: 'POST',
        headers,
        body: formData
      })

      const successful = response.ok || (response.status >= 200 && response.status < 400) || response.redirected

      if (!successful) {
        const text = await response.text()
        throw new Error(text || `Request failed with status ${response.status}`)
      }

      // Clear selection from session storage
      this.clearSelection(storageKey)

      // Redirect or reload
      const targetUrl = response.redirected && response.url ? response.url : window.location.href
      window.location.assign(targetUrl)
    } catch (error) {
      alert(error.message || 'Failed to move users')
    } finally {
      hideLoader()
    }
  }

  getSelectedUserIds(storageKey) {
    try {
      const stored = sessionStorage.getItem(storageKey)
      const ids = stored ? JSON.parse(stored) : []
      return Array.isArray(ids) ? ids : []
    } catch {
      return []
    }
  }

  clearSelection(storageKey) {
    try {
      sessionStorage.removeItem(storageKey)
    } catch {
      // no-op
    }
  }

  showWarning(message) {
    if (this.hasWarningContainerTarget && this.hasWarningTextTarget) {
      this.warningTextTarget.textContent = message
      this.warningContainerTarget.style.display = 'block'
    }
  }

  hideWarning() {
    if (this.hasWarningContainerTarget) {
      this.warningContainerTarget.style.display = 'none'
    }
  }
}
